import { type ReactNode, type HTMLAttributes } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export type BadgeVariant =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'outline'
  | 'ghost'
  | 'neutral'
  | 'info';

export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** Visual variant */
  variant?: BadgeVariant;
  /** Badge size */
  size?: BadgeSize;
  /** Custom background color (hex or Tailwind class) */
  color?: string;
  /** Icon to show before text */
  icon?: ReactNode;
  children: ReactNode;
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-neutral-100 text-neutral-700',
  primary: 'bg-primary-100 text-primary-700',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-amber-100 text-amber-700',
  danger: 'bg-red-100 text-red-700',
  outline: 'bg-transparent border border-neutral-300 text-neutral-600',
  ghost: 'bg-transparent text-neutral-500',
  neutral: 'bg-neutral-100 text-neutral-600',
  info: 'bg-blue-100 text-blue-700',
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'text-2xs px-1.5 py-0.5',
  md: 'text-xs px-2 py-0.5',
  lg: 'text-sm px-2.5 py-1',
};

/**
 * Badge component for status indicators and tags
 *
 * @example
 * ```tsx
 * <Badge variant="success">Active</Badge>
 * <Badge variant="warning" icon={<AlertIcon />}>Needs Review</Badge>
 * <Badge color="#8b5cf6">Custom</Badge>
 * ```
 */
export function Badge({
  variant = 'default',
  size = 'md',
  color,
  icon,
  className,
  children,
  ...props
}: BadgeProps) {
  const customStyle = color
    ? {
        backgroundColor: color.startsWith('#') ? `${color}20` : undefined,
        color: color.startsWith('#') ? color : undefined,
      }
    : undefined;

  return (
    <span
      className={twMerge(
        clsx(
          'inline-flex items-center gap-1 font-medium rounded-full whitespace-nowrap',
          !color && variantClasses[variant],
          sizeClasses[size],
          className
        )
      )}
      style={customStyle}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </span>
  );
}

// Specialized badge variants for skill-specific use cases

export interface VerdictBadgeProps extends Omit<BadgeProps, 'variant' | 'color'> {
  verdict: 'strong' | 'promising' | 'needs_work' | 'misaligned';
}

const verdictConfig: Record<VerdictBadgeProps['verdict'], { variant: BadgeVariant; label: string }> = {
  strong: { variant: 'success', label: 'Strong' },
  promising: { variant: 'success', label: 'Promising' },
  needs_work: { variant: 'warning', label: 'Needs Work' },
  misaligned: { variant: 'danger', label: 'Misaligned' },
};

export function VerdictBadge({ verdict, ...props }: VerdictBadgeProps) {
  const config = verdictConfig[verdict];
  return (
    <Badge variant={config.variant} {...props}>
      {config.label}
    </Badge>
  );
}

export interface PriorityBadgeProps extends Omit<BadgeProps, 'variant' | 'color'> {
  priority: 'critical' | 'high' | 'medium' | 'low';
}

const priorityConfig: Record<PriorityBadgeProps['priority'], { variant: BadgeVariant; label: string }> = {
  critical: { variant: 'danger', label: 'Critical' },
  high: { variant: 'warning', label: 'High' },
  medium: { variant: 'primary', label: 'Medium' },
  low: { variant: 'ghost', label: 'Low' },
};

export function PriorityBadge({ priority, ...props }: PriorityBadgeProps) {
  const config = priorityConfig[priority];
  return (
    <Badge variant={config.variant} {...props}>
      {config.label}
    </Badge>
  );
}
